'use client'

import { type ReactNode, useId } from 'react'

import * as Select from '@radix-ui/react-select'
import { ChevronDown } from 'lucide-react'

import {
  TabsTriggerRenderContext,
  useTabsValue,
} from '~/components/component-library/Tabs/context'
import {
  TABS_SELECT_CONTENT_CLASSNAME,
  TABS_SELECT_TRIGGER_CLASSNAME,
} from '~/components/component-library/Tabs/styles'

// Radix refocuses the trigger once the content unmounts, which would undo a
// consumer that moved focus in onValueChange (e.g. into the new tab's input).
// Focus is on body at that point unless the consumer moved it.
const keepConsumerFocus = (event: Event) => {
  const active = document.activeElement
  if (active && active !== document.body) event.preventDefault()
}

/** Dropdown of a TabsList's triggers, shown in place of the row below `md` */
export const TabsSelect = ({ children }: { children: ReactNode }) => {
  const { value, onValueChange } = useTabsValue()
  const valueId = useId()

  return (
    <Select.Root value={value} onValueChange={onValueChange}>
      <Select.Trigger
        className={TABS_SELECT_TRIGGER_CLASSNAME}
        aria-labelledby={valueId}
      >
        <span className="min-w-0 flex-1 truncate text-left">
          <Select.Value id={valueId}>
            <TabsTriggerRenderContext value="label">
              {children}
            </TabsTriggerRenderContext>
          </Select.Value>
        </span>
        <Select.Icon asChild>
          <ChevronDown className="size-4 flex-shrink-0 text-secondary" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          side="bottom"
          align="start"
          sideOffset={4}
          position="popper"
          collisionPadding={8}
          onCloseAutoFocus={keepConsumerFocus}
          className={TABS_SELECT_CONTENT_CLASSNAME}
        >
          <Select.Viewport>
            <TabsTriggerRenderContext value="option">
              {children}
            </TabsTriggerRenderContext>
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}
